import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { EmptyState, ErrorNotice, Loading } from '../components/Feedback';
import { SenoIcon } from '../components/SenoIcon';
import { get, post, remove } from '../lib/api';

type Reference = {
  id: string;
  type: string;
  title: string;
  authors: string[];
  publicationYear: number | null;
  doi: string | null;
  url: string | null;
  status: 'saved' | 'used';
};

type SearchResult = Omit<Reference, 'id' | 'status'> & {
  provider: 'crossref' | 'openalex';
  externalId: string;
  provenance: Record<string, unknown>;
};

export function ReferencesPanel({ projectId }: { projectId: string }) {
  const [references, setReferences] = useState<Reference[]>([]);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [query, setQuery] = useState('');
  const [provider, setProvider] = useState<'crossref' | 'openalex'>('crossref');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await get<{ references: Reference[] }>(`/api/projects/${projectId}/references`);
      setReferences(data.references);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Pustaka tidak dapat dimuat.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function search(event: FormEvent) {
    event.preventDefault();
    setSearching(true);
    setError('');
    try {
      const data = await post<{ results: SearchResult[] }>(
        `/api/projects/${projectId}/references/search`,
        { query, provider, limit: 8 }
      );
      setResults(data.results);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Pencarian sumber gagal.');
    } finally {
      setSearching(false);
    }
  }

  async function save(result: SearchResult) {
    try {
      await post(`/api/projects/${projectId}/references`, {
        type: result.type,
        title: result.title,
        authors: result.authors,
        publicationYear: result.publicationYear,
        doi: result.doi,
        url: result.url,
        metadata: { ...result.provenance, externalId: result.externalId }
      });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Referensi gagal disimpan.');
    }
  }

  async function uploadReference(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setUploading(true);
    setError('');
    const form = new FormData(formElement);
    const file = form.get('file');
    if (!(file instanceof File)) return;
    const attachmentForm = new FormData();
    attachmentForm.set('file', file);
    attachmentForm.set('category', 'reference-fulltext');
    attachmentForm.set('aiReadable', 'true');
    try {
      const uploaded = await post<{ file: { id: string; name: string } }>(
        `/api/projects/${projectId}/attachments`,
        attachmentForm
      );
      await post(`/api/projects/${projectId}/references`, {
        type: 'user_file',
        title: String(form.get('title') || uploaded.file.name),
        authors: [],
        publicationYear: null,
        doi: null,
        url: null,
        metadata: { attachmentId: uploaded.file.id, uploadedByUser: true }
      });
      formElement.reset();
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Berkas referensi gagal disimpan.');
    } finally {
      setUploading(false);
    }
  }

  async function removeReference(referenceId: string) {
    try {
      await remove(`/api/projects/${projectId}/references/${referenceId}`);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Referensi tidak dapat dihapus.');
    }
  }

  return (
    <section className="materials-panel">
      <div className="section-title">
        <div>
          <span className="eyebrow">Pustaka terverifikasi</span>
          <h2>Referensi penelitian</h2>
          <p>Seno AI hanya boleh menyitir referensi yang tersimpan di proyek ini.</p>
        </div>
        <div className="export-links">
          <a href={`/api/projects/${projectId}/references/export?format=csv`}>CSV</a>
          <a href={`/api/projects/${projectId}/references/export?format=ris`}>RIS</a>
          <a href={`/api/projects/${projectId}/references/export?format=bibtex`}>BibTeX</a>
        </div>
      </div>
      {error ? <ErrorNotice message={error} /> : null}
      <form className="reference-search" onSubmit={search}>
        <label>
          Cari judul, topik, atau penulis
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            required
            minLength={3}
          />
        </label>
        <select
          value={provider}
          onChange={(event) => setProvider(event.target.value as typeof provider)}
        >
          <option value="crossref">Crossref</option>
          <option value="openalex">OpenAlex</option>
        </select>
        <button className="button button-primary" disabled={searching}>
          {searching ? 'Mencari…' : 'Cari sumber'}
        </button>
      </form>
      <form className="reference-upload" onSubmit={uploadReference}>
        <input name="title" placeholder="Judul sumber dari berkas" required minLength={3} />
        <input name="file" type="file" accept=".pdf,.docx" required />
        <button className="button button-secondary" disabled={uploading}>
          <SenoIcon name="bahan" /> {uploading ? 'Mengunggah…' : 'Upload sumber milik Anda'}
        </button>
      </form>
      {results.length ? (
        <div className="source-results">
          {results.map((result) => (
            <article key={`${result.provider}-${result.externalId}`}>
              <div>
                <span className="source-provider">{result.provider}</span>
                <strong>{result.title}</strong>
                <p>
                  {result.authors.join(', ') || 'Penulis tidak tersedia'} ·{' '}
                  {result.publicationYear ?? 'n.d.'}
                </p>
              </div>
              <button className="button button-secondary" onClick={() => save(result)}>
                Simpan
              </button>
            </article>
          ))}
        </div>
      ) : null}
      {loading ? (
        <Loading label="Membuka Pustaka…" />
      ) : references.length === 0 ? (
        <EmptyState
          title="Pustaka masih kosong"
          description="Cari Crossref/OpenAlex lalu simpan sumber yang benar-benar akan digunakan."
        />
      ) : (
        <div className="reference-list">
          {references.map((reference) => (
            <article key={reference.id}>
              <SenoIcon name="referensi" />
              <div>
                <strong>{reference.title}</strong>
                <span>
                  {reference.authors.join(', ')} · {reference.publicationYear ?? 'n.d.'}
                </span>
                {reference.doi ? <small>DOI {reference.doi}</small> : null}
              </div>
              <span
                className={`badge badge-${reference.status === 'used' ? 'active' : 'confirmed'}`}
              >
                {reference.status === 'used' ? 'Dipakai' : 'Tersimpan'}
              </span>
              <button className="text-button danger" onClick={() => removeReference(reference.id)}>
                Hapus
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

type Evidence = {
  id: string;
  kind: string;
  title: string;
  summary: string;
  status: 'draft' | 'confirmed' | 'protected';
};

export function EvidencePanel({ projectId }: { projectId: string }) {
  const [items, setItems] = useState<Evidence[]>([]);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const data = await get<{ evidence: Evidence[] }>(`/api/projects/${projectId}/evidence`);
      setItems(data.evidence);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Evidence tidak dapat dimuat.');
    }
  }, [projectId]);
  useEffect(() => void load(), [load]);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      await post(`/api/projects/${projectId}/evidence`, {
        kind: form.get('kind'),
        title: form.get('title'),
        summary: form.get('summary'),
        data: {},
        status: 'draft'
      });
      formElement.reset();
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Evidence gagal disimpan.');
    }
  }

  // FINAL ALIGNMENT A: explicit evidence lifecycle
  async function advanceEvidence(item: Evidence) {
    const next = item.status === 'draft' ? 'confirmed' : item.status === 'confirmed' ? 'protected' : null;
    if (!next) return;
    try {
      await post(`/api/projects/${projectId}/evidence/${item.id}/status`, { status: next });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Status evidence belum dapat diperbarui.');
    }
  }

  return (
    <section className="materials-panel evidence-panel">
      <div className="section-title">
        <div>
          <span className="eyebrow">Evidence Lock</span>
          <h2>Data dan hasil penelitian</h2>
          <p>BAB IV–V tidak dapat menghasilkan klaim empiris tanpa evidence yang dikonfirmasi.</p>
        </div>
      </div>
      {error ? <ErrorNotice message={error} /> : null}
      <form className="evidence-form" onSubmit={create}>
        <select name="kind" aria-label="Jenis evidence" defaultValue="analysis">
          <option value="analysis">Hasil analisis</option>
          <option value="dataset">Dataset</option>
          <option value="interview">Wawancara</option>
          <option value="experiment">Eksperimen</option>
          <option value="official_statistic">Statistik resmi</option>
          <option value="user_result">Hasil yang dimasukkan pengguna</option>
        </select>
        <input name="title" placeholder="Nama evidence" required minLength={3} />
        <textarea
          name="summary"
          placeholder="Ringkasan fakta/hasil yang boleh digunakan"
          rows={3}
        />
        <button className="button button-primary">Simpan evidence</button>
      </form>
      <div className="evidence-list">
        {items.map((item) => (
          <article key={item.id}>
            <SenoIcon name="data" />
            <div>
              <strong>{item.title}</strong>
              <p>{item.summary || 'Tanpa ringkasan'}</p>
            </div>
            <div className="row-actions compact-actions">
              <span className={`badge badge-${item.status === 'protected' ? 'active' : 'confirmed'}`}>
                {item.status === 'draft'
                  ? 'Draft'
                  : item.status === 'confirmed'
                    ? 'Dikonfirmasi'
                    : 'Dilindungi'}
              </span>
              {item.status !== 'protected' ? (
                <button
                  className="button button-secondary"
                  onClick={() => advanceEvidence(item)}
                >
                  {item.status === 'draft' ? 'Konfirmasi' : 'Lindungi'}
                </button>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
